import { useEffect, useState } from "react";
import { useOrderDesignReview } from "@jasper/use-cases";
import { notifications } from "@mantine/notifications";

type UseDesignReviewActionsParams = {
  orderId: string;
  refetchOrder?: () => Promise<unknown>;
};

// Dentist review of the opened design: validation, or rejection with a reason
// and files (shown with its impact on the delivery date).
export const useDesignReviewActions = ({
  orderId,
  refetchOrder,
}: UseDesignReviewActionsParams) => {
  const [isReviewing, setIsReviewing] = useState(false);

  const [newDeliveryDate, setNewDeliveryDate] = useState<Date | null>(null);
  const [initialDeliveryDate, setInitialDeliveryDate] = useState<Date | null>(
    null
  );
  const [newDeliveryDateError, setNewDeliveryDateError] = useState<
    string | null
  >(null);
  const [isRejectDesignModalOpen, setIsRejectDesignModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionFiles, setRejectionFiles] = useState<File[]>([]);
  const [rejectionError, setRejectionError] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (rejectionReason || rejectionFiles.length > 0) {
      setRejectionError(undefined);
    }
  }, [rejectionReason, rejectionFiles]);

  const {
    openedOrderDesign,
    validateOrderDesign,
    rejectOrderDesign,
    requestedOrderDesign,
    getNewDeliveryDateAfterRejection,
  } = useOrderDesignReview({
    orderId,
  });

  useEffect(() => {
    if (!isRejectDesignModalOpen) {
      return;
    }

    setNewDeliveryDate(null);
    setInitialDeliveryDate(null);
    setNewDeliveryDateError(null);

    getNewDeliveryDateAfterRejection()
      .then(({ newDeliveryDate, initialDeliveryDate }) => {
        newDeliveryDate.setHours(0, 0, 0, 0);
        initialDeliveryDate.setHours(0, 0, 0, 0);

        setNewDeliveryDate(newDeliveryDate);
        setInitialDeliveryDate(initialDeliveryDate);
      })
      .catch(error => {
        setNewDeliveryDateError(error.message);
      });
  }, [isRejectDesignModalOpen]);

  const handleReview = async (review: "VALIDATED" | "REJECTED") => {
    setIsReviewing(true);
    setRejectionError(undefined);

    try {
      if (review === "VALIDATED") {
        await validateOrderDesign();
      } else {
        if (!rejectionReason) {
          setRejectionError("Veuillez fournir une raison pour le rejet.");
          setIsReviewing(false);
          return;
        }

        if (rejectionReason.trim().length < 10) {
          setRejectionError(
            "La raison du rejet doit comporter au moins 10 caractères."
          );
          setIsReviewing(false);
          return;
        }
        await rejectOrderDesign({
          reason: rejectionReason,
          files: rejectionFiles,
        });

        if (refetchOrder) {
          try {
            await refetchOrder();
          } catch (err) {
            console.log("Failed to refetch order after rejecting design", err);
          }
        }
      }

      const message =
        review === "VALIDATED"
          ? "Conception validée avec succès."
          : "Conception rejetée avec succès.";

      notifications.show({
        title: "Revue de conception",
        message,
        color: "green",
      });
    } catch (error) {
      console.error("Failed to review order design", error);
      notifications.show({
        title: "Revue de conception",
        message:
          "Une erreur est survenue lors de la soumission de la revue. Veuillez réessayer.",
        color: "red",
      });
    }

    setIsReviewing(false);
  };

  const closeRejectModal = () => {
    setIsRejectDesignModalOpen(false);
    setRejectionReason("");
    setRejectionFiles([]);
    setRejectionError(undefined);
  };

  return {
    openedOrderDesign,
    requestedOrderDesign,
    isReviewing,
    validate: () => handleReview("VALIDATED"),
    rejection: {
      isOpened: isRejectDesignModalOpen,
      open: () => setIsRejectDesignModalOpen(true),
      close: closeRejectModal,
      submit: () => handleReview("REJECTED"),
      reason: rejectionReason,
      setReason: setRejectionReason,
      files: rejectionFiles,
      setFiles: setRejectionFiles,
      error: rejectionError,
      newDeliveryDate,
      initialDeliveryDate,
      newDeliveryDateError,
    },
  };
};

export type DesignReviewActions = ReturnType<typeof useDesignReviewActions>;
